import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { api, formatCurrency, formatDate } from '../../utils/api'
import { isFloorRefusal, postPriceWithFloorConfirm } from '../../utils/consignmentFloor'
import { SHOPIFY_ADMIN_URL } from '../../utils/constants'
import { MetricsRow, MultiSelectDropdown, SearchLinks, ProductDetailModal, ProgressBar } from '../shared'
import StaffPickButton from '../shared/StaffPickButton'
import { useAuth } from '../../hooks/useAuth'
import { applyColourAndRatio } from '../../utils/imageProcessing'
import { removeBackgroundBody } from '../../utils/removeBackgroundBody'

function InventoryList() {
  const location = useLocation()
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  // All items from API (larger dataset for client-side operations)
  const [allItems, setAllItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [error, setError] = useState(null)
  const [productTypes, setProductTypes] = useState([])
  const [vendors, setVendors] = useState([])
  const [collections, setCollections] = useState([])
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [loadingProduct, setLoadingProduct] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [selectedItems, setSelectedItems] = useState(new Set())
  const [processing, setProcessing] = useState(false)
  const [progress, setProgress] = useState({ current: 0, total: 0, status: '' })
  const abortControllerRef = useRef(null)
  const [editedPrices, setEditedPrices] = useState({})
  const [processedPhotos, setProcessedPhotos] = useState({})
  const [bulkPhotoStep, setBulkPhotoStep] = useState('')
  const [relistProduct, setRelistProduct] = useState(null)
  const [pendingAutoSearch, setPendingAutoSearch] = useState(false)
  const [relistQty, setRelistQty] = useState(1)
  const [relisting, setRelisting] = useState(false)

  // Search filters (trigger API call)
  // Note: sku filter, sku column, and sku CSV field have all been removed
  // — Shopify SKU is no longer authoritative for warehouse location and
  // surfacing it tempted operators to read it as the shelf. Location now
  // lives in item_location; search by shelf via /warehouse/putaway.
  const [searchFilters, setSearchFilters] = useState({
    product_id: '',
    title: '',
    tags: '',
    product_types: [],
    vendor: '',
    collection_ids: [],
    stock_status: 'in_stock',
    price_min: '',
    price_max: '',
    created_from: '',
    created_to: '',
    published_from: '',
    published_to: ''
  })

  // Display options (client-side only)
  const [sortBy, setSortBy] = useState('updated_at')
  const [sortOrder, setSortOrder] = useState('desc')
  const [limit, setLimit] = useState(25)
  const [page, setPage] = useState(1)

  useEffect(() => {
    loadReferenceData()
  }, [])

  // Watch for productId in URL (for pinned items navigation)
  // Using location.key ensures this runs on every navigation, even to the same URL
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const productIdParam = params.get('productId')
    if (productIdParam) {
      loadAndOpenProduct(productIdParam)
      // Clean up URL using React Router's navigate to keep router state in sync
      navigate(location.pathname, { replace: true })
      return
    }
    // ?q= is the label deep-link fallback: an id that didn't resolve lands in
    // the ID search box and searches, so the operator sees the miss in context.
    const qParam = params.get('q')
    if (qParam) {
      setSearchFilters(prev => ({ ...prev, product_id: qParam, stock_status: 'all' }))
      setPendingAutoSearch(true)
      navigate(location.pathname, { replace: true })
    }
  }, [location.key])

  async function loadAndOpenProduct(productId) {
    try {
      setLoadingProduct(true)
      // Strip any # prefix and extract just the numeric ID
      const cleanId = String(productId).replace(/^#/, '').replace(/^gid:\/\/shopify\/Product\//, '')
      // Search for this specific product
      const response = await api.get(`/api/inventory/shopify/search?product_id=${cleanId}&stock_status=all`)
      const products = response.products || []
      if (products.length > 0) {
        setSelectedProduct(products[0])
      } else {
        console.warn('Product not found:', cleanId)
      }
    } catch (error) {
      console.error('Error loading product:', error)
    } finally {
      setLoadingProduct(false)
    }
  }

  async function loadReferenceData() {
    try {
      const [typesRes, vendorsRes, collectionsRes] = await Promise.all([
        api.get('/api/inventory/product-types'),
        api.get('/api/inventory/shopify/vendors'),
        api.get('/api/inventory/shopify/collections')
      ])
      setProductTypes(typesRes.productTypes || [])
      setVendors(vendorsRes.vendors || [])
      setCollections(collectionsRes.collections || [])
    } catch (err) {
      console.error('Failed to load reference data:', err)
    }
  }

  const loadItems = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (searchFilters.product_id) params.set('product_id', searchFilters.product_id)
      if (searchFilters.title) params.set('title', searchFilters.title)
      if (searchFilters.tags) params.set('tags', searchFilters.tags)
      if (searchFilters.product_types.length > 0) params.set('product_types', searchFilters.product_types.join(','))
      if (searchFilters.vendor) params.set('vendor', searchFilters.vendor)
      if (searchFilters.collection_ids.length > 0) params.set('collection_ids', searchFilters.collection_ids.join(','))
      params.set('stock_status', searchFilters.stock_status)
      if (searchFilters.price_min) params.set('price_min', searchFilters.price_min)
      if (searchFilters.price_max) params.set('price_max', searchFilters.price_max)
      if (searchFilters.created_from) params.set('created_from', searchFilters.created_from)
      if (searchFilters.created_to) params.set('created_to', searchFilters.created_to)
      // published_at = when an item went live. Filter on it to find drafts
      // activated in a window; created_at keeps the original draft date.
      if (searchFilters.published_from) params.set('published_from', searchFilters.published_from)
      if (searchFilters.published_to) params.set('published_to', searchFilters.published_to)
      // Always fetch max for client-side sorting/pagination
      params.set('limit', '500')

      const response = await api.get(`/api/inventory/shopify/search?${params}`)
      setAllItems(response.products || [])
      setTotalCount(response.count || 0)
      setSelectedItems(new Set())
      setEditedPrices({})
      setProcessedPhotos({})
    } catch (err) {
      console.error('Failed to load inventory:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [searchFilters])

  // Runs one render after the ?q= filter lands, so loadItems' closure has the
  // updated searchFilters rather than the stale ones from the same tick.
  // Must sit BELOW the loadItems declaration: the dependency array is
  // evaluated during render, and `const loadItems` is TDZ until then.
  useEffect(() => {
    if (!pendingAutoSearch) return
    setPendingAutoSearch(false)
    setHasSearched(true)
    setPage(1)
    loadItems()
  }, [pendingAutoSearch, loadItems])

  function handleFilterChange(field, value) {
    setSearchFilters(prev => ({ ...prev, [field]: value }))
  }

  function handleSearch(e) {
    e.preventDefault()
    setHasSearched(true)
    setPage(1)
    loadItems()
  }

  function clearFilters() {
    setSearchFilters({
      product_id: '',
      title: '',
      tags: '',
      product_types: [],
      vendor: '',
      collection_ids: [],
      stock_status: 'in_stock',
      price_min: '',
      price_max: '',
      created_from: '',
      created_to: '',
      published_from: '',
      published_to: ''
    })
    setSortBy('updated_at')
    setSortOrder('desc')
    setLimit(25)
    setPage(1)
  }

  // Client-side sorting and pagination
  const { displayItems, totalPages } = useMemo(() => {
    const sorted = [...allItems].sort((a, b) => {
      let aVal = a[sortBy]
      let bVal = b[sortBy]

      // Handle numeric fields
      if (['price', 'cost', 'quantity', 'compare_at_price'].includes(sortBy)) {
        aVal = parseFloat(aVal) || 0
        bVal = parseFloat(bVal) || 0
      }

      // Handle string fields
      if (typeof aVal === 'string') aVal = aVal.toLowerCase()
      if (typeof bVal === 'string') bVal = bVal.toLowerCase()

      // Handle dates
      if (['created_at', 'updated_at'].includes(sortBy)) {
        aVal = new Date(aVal).getTime()
        bVal = new Date(bVal).getTime()
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1
      return 0
    })

    const totalPages = Math.ceil(sorted.length / limit)
    const start = (page - 1) * limit
    const items = sorted.slice(start, start + limit)
    return { displayItems: items, totalPages }
  }, [allItems, sortBy, sortOrder, limit, page])

  function handleSort(field) {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('asc')
    }
  }

  function SortHeader({ field, children, align, className = '' }) {
    const isActive = sortBy === field
    const arrow = isActive ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : ''
    return (
      <th
        onClick={() => handleSort(field)}
        style={{ cursor: 'pointer', textAlign: align, userSelect: 'none' }}
        className={`${isActive ? 'sort-active' : ''} ${className}`}
      >
        {children}{arrow}
      </th>
    )
  }

  async function handleRowClick(productId) {
    setLoadingProduct(true)
    try {
      const response = await api.get(`/api/inventory/shopify/product/${productId}`)
      setSelectedProduct(response.product)
    } catch (err) {
      console.error('Failed to load product:', err)
      alert('Failed to load product details')
    } finally {
      setLoadingProduct(false)
    }
  }

  async function handleProductSave(productId, updates) {
    try {
      // Price updates go via POST endpoint (which handles variant pricing).
      // D15: a below-floor consignment price warns-and-confirms, never blocks.
      if (updates.price !== undefined) {
        await postPriceWithFloorConfirm(`/api/inventory/shopify/update/${productId}`, { price: updates.price })
      }
      // Send remaining fields via PUT endpoint (quantity is handled there too)
      const { price, ...otherUpdates } = updates
      if (Object.keys(otherUpdates).length > 0) {
        await api.put(`/api/inventory/shopify/product/${productId}`, otherUpdates)
      }
      const response = await api.get(`/api/inventory/shopify/product/${productId}`)
      setSelectedProduct(response.product)
      loadItems()
    } catch (err) {
      console.error('Failed to save product:', err)
      throw err
    }
  }

  async function handleProductDelete(productId) {
    try {
      await api.delete(`/api/inventory/${productId}`)
      setSelectedProduct(null)
      loadItems()
    } catch (err) {
      console.error('Failed to delete product:', err)
      throw err
    }
  }

  function handleRelistClick(product) {
    setSelectedProduct(null)
    setRelistProduct(product)
    setRelistQty(1)
  }

  async function handleRelistConfirm() {
    if (!relistProduct) return
    setRelisting(true)
    try {
      await api.post(`/api/inventory/shopify/relist/${relistProduct.id}`, { quantity: relistQty })
      setRelistProduct(null)
      loadItems()
    } catch (err) {
      console.error('Failed to relist product:', err)
      alert('Failed to relist product: ' + (err.message || 'Unknown error'))
    } finally {
      setRelisting(false)
    }
  }

  function handleSelectAll(e) {
    if (e.target.checked) {
      setSelectedItems(new Set(displayItems.map(item => item.id)))
    } else {
      setSelectedItems(new Set())
    }
  }

  function handleSelectItem(id, e) {
    e.stopPropagation()
    setSelectedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) newSet.delete(id)
      else newSet.add(id)
      return newSet
    })
  }

  function handleExportCsv() {
    const items = allItems
    if (items.length === 0) return
    const headers = ['Product ID', 'Title', 'Type', 'Vendor', 'Qty', 'Cost', 'Price']
    const escCsv = (val) => {
      const str = String(val ?? '')
      return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"` : str
    }
    const rows = items.map(item => [
      item.id,
      escCsv(item.title),
      escCsv(item.product_type),
      escCsv(item.vendor),
      item.quantity ?? 0,
      parseFloat(item.cost || 0).toFixed(2),
      parseFloat(item.price || 0).toFixed(2)
    ].join(','))
    const csv = [headers.join(','), ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `inventory_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  async function handleChangeToDraft() {
    if (selectedItems.size === 0) return

    const confirmed = window.confirm(
      `Are you sure you want to change ${selectedItems.size} products to draft status?\n\nThis will remove them from your storefront.`
    )
    if (!confirmed) return

    setProcessing(true)
    abortControllerRef.current = new AbortController()
    const ids = Array.from(selectedItems)
    let completed = 0
    const errors = []

    for (const id of ids) {
      if (abortControllerRef.current.signal.aborted) break

      setProgress({
        current: completed + 1,
        total: ids.length,
        status: `Processing ${completed + 1} of ${ids.length}...`
      })

      try {
        await api.post(`/api/inventory/shopify/draft/${id}`)
        completed++
      } catch (err) {
        errors.push({ id, error: err.message })
        completed++
      }
    }

    setProcessing(false)
    setProgress({ current: 0, total: 0, status: '' })

    if (errors.length > 0) {
      alert(`Completed with ${errors.length} errors. ${completed - errors.length} products changed to draft.`)
    } else {
      alert(`Successfully changed ${completed} products to draft.`)
    }

    loadItems()
  }

  async function handleRefreshProducts() {
    if (selectedItems.size === 0) return

    const confirmed = window.confirm(
      `Refresh ${selectedItems.size} product(s)?\n\nThis will:\n• Create new draft copies with AI-rewritten titles\n• Set original product quantities to 0\n• Keep originals active (for redirect/reference)`
    )
    if (!confirmed) return

    setProcessing(true)
    abortControllerRef.current = new AbortController()
    const ids = Array.from(selectedItems)
    let completed = 0
    const results = []
    const errors = []

    for (const id of ids) {
      if (abortControllerRef.current.signal.aborted) break

      setProgress({
        current: completed + 1,
        total: ids.length,
        status: `Refreshing ${completed + 1} of ${ids.length}...`
      })

      try {
        const result = await api.post(`/api/inventory/shopify/refresh/${id}`)
        results.push(result)
        completed++
      } catch (err) {
        errors.push({ id, error: err.message })
        completed++
      }
    }

    setProcessing(false)
    setProgress({ current: 0, total: 0, status: '' })
    setSelectedItems(new Set())

    if (results.length > 0) {
      const summary = results.map(r =>
        `${r.old_title} → ${r.new_title}`
      ).join('\n')

      if (errors.length > 0) {
        alert(`Refreshed ${results.length} product(s) with ${errors.length} error(s).\n\nTitle changes:\n${summary}`)
      } else {
        alert(`Successfully refreshed ${results.length} product(s).\n\nTitle changes:\n${summary}`)
      }
    } else if (errors.length > 0) {
      alert(`All ${errors.length} product(s) failed to refresh.`)
    }

    loadItems()
  }

  function handleCancelOperation() {
    if (abortControllerRef.current) abortControllerRef.current.abort()
  }

  async function handleSaveAllPrices() {
    const entries = Object.entries(editedPrices)
    if (entries.length === 0) return

    const confirmed = window.confirm(
      `Update prices for ${entries.length} product(s) on Shopify?`
    )
    if (!confirmed) return

    setProcessing(true)
    abortControllerRef.current = new AbortController()

    const batches = []
    for (let i = 0; i < entries.length; i += 5) {
      batches.push(entries.slice(i, i + 5).map(([productId, price]) => ({ productId, price })))
    }

    let completed = 0
    const errors = []

    for (const batch of batches) {
      if (abortControllerRef.current.signal.aborted) break

      setProgress({
        current: completed,
        total: entries.length,
        status: `Updating prices ${completed + 1}-${Math.min(completed + batch.length, entries.length)} of ${entries.length}...`
      })

      try {
        let response
        try {
          response = await api.post('/api/inventory/shopify/batch-update-prices', { items: batch })
        } catch (err) {
          // D15: the batch was refused whole (nothing written) because a
          // consignment item is below its seller's floor — confirm and retry.
          if (!isFloorRefusal(err)) throw err
          if (!window.confirm(`${err.message}\n\nUpdate these prices anyway?`)) throw err
          response = await api.post('/api/inventory/shopify/batch-update-prices', { items: batch, confirmBelowFloor: true })
        }
        for (const result of response.results) {
          completed++
          if (!result.success) {
            errors.push({ id: result.productId, error: result.error })
          }
        }
      } catch (err) {
        for (const item of batch) {
          completed++
          errors.push({ id: item.productId, error: err.message })
        }
      }
    }

    setProcessing(false)
    setProgress({ current: 0, total: 0, status: '' })

    if (errors.length > 0) {
      alert(`Completed with ${errors.length} error(s). ${completed - errors.length} price(s) updated successfully.`)
    } else {
      alert(`Successfully updated ${completed} price(s).`)
    }

    setEditedPrices({})
    loadItems()
  }

  async function handleBulkProcessPhotos() {
    if (selectedItems.size === 0) return

    const confirmed = window.confirm(
      `Process lead photos for ${selectedItems.size} product(s)?\n\nThis will remove backgrounds, apply colour correction, and convert to 4:5 portrait ratio.`
    )
    if (!confirmed) return

    setProcessing(true)
    abortControllerRef.current = new AbortController()
    const ids = Array.from(selectedItems)
    let completed = 0
    const errors = []
    const newProcessed = {}

    for (const id of ids) {
      if (abortControllerRef.current.signal.aborted) break

      const item = allItems.find(i => i.id === id)
      const itemTitle = item?.title || `Product ${id}`

      setProgress({
        current: completed + 1,
        total: ids.length,
        status: `Processing ${completed + 1} of ${ids.length}: ${itemTitle}`
      })

      if (!item?.images || item.images.length === 0) {
        errors.push({ id, error: 'No images' })
        completed++
        continue
      }

      const leadPhoto = item.images[0]

      try {
        const response = await api.post('/api/image/remove-background', await removeBackgroundBody(leadPhoto.src))

        if (!response.success || !response.image_data) {
          errors.push({ id, error: 'BG removal failed' })
          completed++
          continue
        }

        const finalBase64 = await applyColourAndRatio(response.image_data, response.content_type || 'image/png')

        newProcessed[id] = {
          imageData: finalBase64,
          imageId: leadPhoto.id,
          filename: `processed-lead-${id}.png`,
          position: 1,
          title: itemTitle
        }
        completed++
      } catch (err) {
        errors.push({ id, error: err.message })
        completed++
      }

      // Delay between requests to avoid remove.bg rate limiting
      if (completed < ids.length) {
        await new Promise(r => setTimeout(r, 1000))
      }
    }

    setProcessing(false)
    setProgress({ current: 0, total: 0, status: '' })
    setProcessedPhotos(prev => ({ ...prev, ...newProcessed }))

    const successCount = Object.keys(newProcessed).length
    if (errors.length > 0) {
      alert(`Processing complete: ${successCount} succeeded, ${errors.length} failed.\n\nErrors:\n${errors.map(e => `- ${e.error}`).join('\n')}`)
    } else {
      alert(`Successfully processed ${successCount} photo(s). Click "Save Photos to Shopify" to upload.`)
    }
  }

  async function handleSaveProcessedPhotos() {
    const entries = Object.entries(processedPhotos)
    if (entries.length === 0) return

    const confirmed = window.confirm(
      `Upload ${entries.length} processed photo(s) to Shopify?\n\nThis will replace the lead image for each product.`
    )
    if (!confirmed) return

    setProcessing(true)
    abortControllerRef.current = new AbortController()
    let completed = 0
    const errors = []

    for (const [productId, photo] of entries) {
      if (abortControllerRef.current.signal.aborted) break

      setProgress({
        current: completed + 1,
        total: entries.length,
        status: `Uploading ${completed + 1} of ${entries.length}: ${photo.title}`
      })

      try {
        await api.post(`/api/inventory/shopify/product/${productId}/image`, {
          image_id: photo.imageId,
          image_data: photo.imageData,
          filename: photo.filename,
          position: photo.position
        })
        completed++
      } catch (err) {
        errors.push({ id: productId, error: err.message })
        completed++
      }
    }

    setProcessing(false)
    setProgress({ current: 0, total: 0, status: '' })

    if (errors.length > 0) {
      alert(`Upload complete with ${errors.length} error(s). ${completed - errors.length} photo(s) saved successfully.`)
    } else {
      alert(`Successfully uploaded ${completed} photo(s) to Shopify.`)
    }

    setProcessedPhotos({})
    loadItems()
  }

  // Check if advanced filters are active
  const hasAdvancedFilters = searchFilters.product_types.length > 0 || searchFilters.vendor ||
    searchFilters.collection_ids.length > 0 || searchFilters.price_min || searchFilters.price_max ||
    searchFilters.created_from || searchFilters.created_to ||
    searchFilters.published_from || searchFilters.published_to

  // Calculate metrics from displayed items
  const totalValue = displayItems.reduce((sum, item) => sum + (parseFloat(item.price) || 0) * (item.quantity || 0), 0)
  const totalCost = displayItems.reduce((sum, item) => sum + (parseFloat(item.cost) || 0) * (item.quantity || 0), 0)
  const totalQty = displayItems.reduce((sum, item) => sum + (item.quantity || 0), 0)

  const metrics = [
    { label: 'Loaded', value: allItems.length, color: 'blue' },
    { label: 'Total Qty', value: totalQty, color: 'purple' },
    { label: 'Total Value', value: totalValue, format: 'currency', color: 'green' },
    { label: 'Total Cost', value: totalCost, format: 'currency', color: 'amber' }
  ]

  const productTypeOptions = productTypes.map(t => ({ value: t, label: t }))
  const collectionOptions = collections.map(c => ({ value: c.id, label: c.title }))

  return (
    <div>
      <MetricsRow metrics={metrics} />

      <form onSubmit={handleSearch}>
        <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
          {/* Main Filters - Always Visible */}
          <div className="filter-section">
            <div className="filter-group">
              <label className="filter-label">Product ID</label>
              <input
                type="text"
                className="form-input"
                placeholder="Shopify ID..."
                value={searchFilters.product_id}
                onChange={e => handleFilterChange('product_id', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">Title</label>
              <input
                type="text"
                className="form-input"
                placeholder="Search by title..."
                value={searchFilters.title}
                onChange={e => handleFilterChange('title', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">Tag</label>
              <input
                type="text"
                className="form-input"
                placeholder="Search tag..."
                value={searchFilters.tags}
                onChange={e => handleFilterChange('tags', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">Stock Status</label>
              <select
                className="form-input"
                value={searchFilters.stock_status}
                onChange={e => handleFilterChange('stock_status', e.target.value)}
              >
                <option value="all">All Active</option>
                <option value="in_stock">In Stock</option>
                <option value="out_of_stock">Out of Stock</option>
                <option value="unlisted">Unlisted</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          {/* Advanced Toggle */}
          <button
            type="button"
            className="filter-toggle"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            <span className={`filter-toggle-icon ${showAdvanced ? 'expanded' : ''}`}>▼</span>
            {showAdvanced ? 'Hide' : 'Show'} Advanced Search
            {hasAdvancedFilters && <span className="filter-toggle-active">(filters active)</span>}
          </button>

          {/* Advanced Filters */}
          {showAdvanced && (
            <div className="advanced-filters">
              <div className="filter-section">
                <div className="filter-group">
                  <label className="filter-label">Product Type</label>
                  <MultiSelectDropdown
                    options={productTypeOptions}
                    selected={searchFilters.product_types}
                    onChange={values => handleFilterChange('product_types', values)}
                    placeholder="All Types"
                  />
                </div>
                <div className="filter-group">
                  <label className="filter-label">Vendor</label>
                  <select
                    className="form-input"
                    value={searchFilters.vendor}
                    onChange={e => handleFilterChange('vendor', e.target.value)}
                  >
                    <option value="">All Vendors</option>
                    {vendors.map(v => (
                      <option key={v.name} value={v.name}>{v.name} ({v.count})</option>
                    ))}
                  </select>
                </div>
                <div className="filter-group">
                  <label className="filter-label">Collection</label>
                  <MultiSelectDropdown
                    options={collectionOptions}
                    selected={searchFilters.collection_ids}
                    onChange={values => handleFilterChange('collection_ids', values)}
                    placeholder="All Collections"
                  />
                </div>
                <div className="filter-group">
                  <label className="filter-label">Min Price</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="0.00"
                    value={searchFilters.price_min}
                    onChange={e => handleFilterChange('price_min', e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label className="filter-label">Max Price</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="99999"
                    value={searchFilters.price_max}
                    onChange={e => handleFilterChange('price_max', e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label className="filter-label">Created From</label>
                  <input
                    type="date"
                    className="form-input"
                    value={searchFilters.created_from}
                    onChange={e => handleFilterChange('created_from', e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label className="filter-label">Created To</label>
                  <input
                    type="date"
                    className="form-input"
                    value={searchFilters.created_to}
                    onChange={e => handleFilterChange('created_to', e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label className="filter-label">Published From</label>
                  <input
                    type="date"
                    className="form-input"
                    value={searchFilters.published_from}
                    onChange={e => handleFilterChange('published_from', e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label className="filter-label">Published To</label>
                  <input
                    type="date"
                    className="form-input"
                    value={searchFilters.published_to}
                    onChange={e => handleFilterChange('published_to', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="filter-actions" style={{ justifyContent: 'flex-start', gap: 'var(--space-3)' }}>
            <button type="button" className="btn btn-secondary" onClick={clearFilters}>
              Clear
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Loading...' : 'Search'}
            </button>
            {allItems.length > 0 && (
              <span className="text-sm text-muted" style={{ marginLeft: 'auto' }}>
                Showing {displayItems.length} of {allItems.length} loaded ({totalCount} total)
              </span>
            )}
          </div>
        </div>
      </form>

      {processing && (
        <ProgressBar
          current={progress.current}
          total={progress.total}
          status={progress.status}
          onCancel={handleCancelOperation}
        />
      )}

      {/* Bulk Actions */}
      {displayItems.length > 0 && (
        <div className="card" style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
            <button
              className="btn btn-secondary"
              onClick={handleExportCsv}
            >
              Export CSV ({allItems.length})
            </button>
            <span className="text-sm" style={{ fontWeight: 500, color: 'var(--color-primary)' }}>
              {selectedItems.size} selected
            </span>
            <button
              className="btn btn-secondary"
              onClick={handleChangeToDraft}
              disabled={selectedItems.size === 0 || processing}
            >
              Change to Draft ({selectedItems.size})
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleBulkProcessPhotos}
              disabled={selectedItems.size === 0 || processing}
            >
              Process Photos ({selectedItems.size})
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleRefreshProducts}
              disabled={selectedItems.size === 0 || processing}
            >
              Refresh Selected ({selectedItems.size})
            </button>
            {hasPermission('inventory') && Object.keys(editedPrices).length > 0 && (
              <button
                className="btn btn-primary"
                onClick={handleSaveAllPrices}
                disabled={processing}
              >
                Save All Prices ({Object.keys(editedPrices).length})
              </button>
            )}
            {Object.keys(processedPhotos).length > 0 && (
              <button
                className="btn btn-primary"
                onClick={handleSaveProcessedPhotos}
                disabled={processing}
              >
                Save Photos to Shopify ({Object.keys(processedPhotos).length})
              </button>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="card">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
            <div className="loading-spinner" />
          </div>
        ) : !hasSearched ? (
          <div className="empty-state">
            <div className="empty-state-title">Search Inventory</div>
            <div className="empty-state-description">Use the filters above and click "Search" to find products.</div>
          </div>
        ) : displayItems.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">No Products Found</div>
            <div className="empty-state-description">Try adjusting your search filters.</div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 40 }} className="col-mobile-hide">
                  <input
                    type="checkbox"
                    checked={selectedItems.size === displayItems.length && displayItems.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th style={{ width: 60 }} className="col-mobile-hide">Image</th>
                <SortHeader field="id" className="col-mobile-hide">ID</SortHeader>
                <SortHeader field="title">Title</SortHeader>
                <SortHeader field="product_type" className="col-mobile-hide">Type</SortHeader>
                <SortHeader field="vendor" className="col-mobile-hide">Vendor</SortHeader>
                <SortHeader field="quantity" align="center" className="col-mobile-hide">Qty</SortHeader>
                <SortHeader field="cost" align="right" className="col-mobile-hide">Cost</SortHeader>
                <SortHeader field="price" align="right" className="col-mobile-hide">Price</SortHeader>
                <th style={{ width: 120 }} className="col-mobile-hide">Search</th>
              </tr>
            </thead>
            <tbody>
              {displayItems.map(item => (
                <tr
                  key={item.id}
                  onClick={() => handleRowClick(item.id)}
                  style={{ cursor: 'pointer' }}
                  className={`${loadingProduct ? 'loading' : ''} ${selectedItems.has(item.id) ? 'row-selected' : ''}`}
                >
                  <td onClick={e => e.stopPropagation()} className="col-mobile-hide">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(item.id)}
                      onChange={e => handleSelectItem(item.id, e)}
                    />
                  </td>
                  <td onClick={e => e.stopPropagation()} className="col-mobile-hide">
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      {item.images && item.images[0] ? (
                        <img
                          src={item.images[0].src}
                          alt=""
                          style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 'var(--radius-sm)' }}
                        />
                      ) : (
                        <div style={{ width: 48, height: 48, background: 'var(--color-gray-100)', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-gray-400)', fontSize: 12 }}>
                          No img
                        </div>
                      )}
                      {processedPhotos[item.id] && (
                        <div style={{ position: 'absolute', top: -3, right: -3, width: 12, height: 12, borderRadius: '50%', background: '#22c55e', border: '2px solid white' }} title="Photo processed — ready to save" />
                      )}
                    </div>
                  </td>
                  <td className="text-sm text-muted col-mobile-hide" onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(item.id) }} style={{ fontFamily: 'monospace', fontSize: 11, cursor: 'copy' }} title="Click to copy">{item.id}</td>
                  <td className="col-mobile-primary">
                    <div style={{ fontWeight: 500, maxWidth: 350, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.title}
                      {/* Ownership badge, tag-driven (spec D7/D12): the consignment
                          tag is stamped at draft creation, never inferred from SKU. */}
                      {Array.isArray(item.tags) && item.tags.includes('consignment') && (
                        <span className="badge badge-purple" style={{ marginLeft: 6, fontSize: 10 }} title="Consignment — we sell this for the customer">
                          Consignment
                        </span>
                      )}
                    </div>
                    <div className="mobile-row-meta" style={{ display: 'none' }}>
                      <span>{item.product_type || 'No type'}</span>
                      <span>Qty: {item.quantity}</span>
                      <span>{formatCurrency(item.price)}</span>
                    </div>
                    {item.tags && item.tags.length > 0 && (
                      <div style={{ display: 'flex', gap: 'var(--space-1)', marginTop: 'var(--space-1)', flexWrap: 'wrap' }}>
                        {item.tags.slice(0, 3).map(tag => (
                          <span key={tag} className="badge badge-neutral" style={{ fontSize: 10 }}>{tag}</span>
                        ))}
                        {item.tags.length > 3 && <span className="text-sm text-muted">+{item.tags.length - 3}</span>}
                      </div>
                    )}
                  </td>
                  <td className="text-sm col-mobile-hide">{item.product_type || '—'}</td>
                  <td className="text-sm col-mobile-hide">{item.vendor || '—'}</td>
                  <td style={{ textAlign: 'center' }} className="col-mobile-hide">
                    <span className={item.quantity > 0 ? '' : 'text-muted'}>{item.quantity}</span>
                  </td>
                  <td className="text-sm col-mobile-hide" style={{ textAlign: 'right' }}>{formatCurrency(item.cost)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 500 }} className="col-mobile-hide" onClick={hasPermission('inventory') ? e => e.stopPropagation() : undefined}>
                    {hasPermission('inventory') ? (
                      <>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editedPrices[item.id] !== undefined ? editedPrices[item.id] : (item.price || '')}
                          onChange={e => {
                            const val = e.target.value
                            setEditedPrices(prev => {
                              // If value matches original, remove from edited
                              if (val === (item.price || '').toString()) {
                                const { [item.id]: _, ...rest } = prev
                                return rest
                              }
                              return { ...prev, [item.id]: val }
                            })
                          }}
                          style={{
                            width: 80,
                            textAlign: 'right',
                            padding: '2px 4px',
                            fontSize: '0.85rem',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-sm)',
                            background: editedPrices[item.id] !== undefined ? '#fefce8' : 'transparent'
                          }}
                        />
                        {item.compare_at_price && (
                          <div className="text-sm text-muted" style={{ textDecoration: 'line-through' }}>
                            {formatCurrency(item.compare_at_price)}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {formatCurrency(item.price)}
                        {item.compare_at_price && (
                          <div className="text-sm text-muted" style={{ textDecoration: 'line-through' }}>
                            {formatCurrency(item.compare_at_price)}
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  <td onClick={e => e.stopPropagation()} className="col-mobile-hide">
                    <SearchLinks title={item.title} />
                    {hasPermission('marketing') && (
                      <div style={{ marginTop: 6 }}>
                        <StaffPickButton
                          productId={item.id}
                          title={item.title}
                          handle={item.handle}
                          priceCents={item.price ? Math.round(parseFloat(item.price) * 100) : null}
                          imageUrl={item.images && item.images[0] ? item.images[0].src : null}
                        />
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {displayItems.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--space-3)', borderTop: '1px solid var(--color-border)' }}>
            <span className="text-sm text-muted">
              Showing {(page - 1) * limit + 1}-{Math.min(page * limit, allItems.length)} of {allItems.length}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              {/* Page navigation */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Back
                </button>
                <span className="text-sm" style={{ minWidth: 80, textAlign: 'center' }}>
                  Page {page} of {totalPages || 1}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Next
                </button>
              </div>
              {/* Per page selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', borderLeft: '1px solid var(--color-border)', paddingLeft: 'var(--space-3)' }}>
                <span className="text-sm text-muted">Per page:</span>
                {[25, 50, 100].map(num => (
                  <button
                    key={num}
                    type="button"
                    className={`btn btn-sm ${limit === num ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => { setLimit(num); setPage(1); }}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          collections={collections}
          onClose={() => setSelectedProduct(null)}
          onSave={handleProductSave}
          onDelete={hasPermission('inventory') ? handleProductDelete : undefined}
          onRelist={hasPermission('inventory') ? handleRelistClick : undefined}
          canEditPrice={hasPermission('inventory')}
          canEditQty={hasPermission('inventory')}
        />
      )}

      {relistProduct && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Relist Product</h3>
              <button className="modal-close" onClick={() => setRelistProduct(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 16, padding: '8px 12px', background: '#f5f5f5', borderRadius: 4 }}>{relistProduct.title}</p>
              <div className="form-group">
                <label className="form-label">Quantity</label>
                <input
                  type="number"
                  className="form-input"
                  style={{ width: 80 }}
                  min="1"
                  value={relistQty}
                  onChange={e => setRelistQty(parseInt(e.target.value) || 1)}
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => setRelistProduct(null)} disabled={relisting}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleRelistConfirm} disabled={relisting}>
                {relisting ? 'Creating...' : 'Create Draft'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default InventoryList
